import { createSelector } from 'reselect';
// Import state types.
import {
	AppState,
} from '@app/redux/states';
// Import selectors.
import {
	makeGetPublicPolicyViewMortgageeGuidsSelector,
	makeIsPublicPolicyActiveForChangesSelector,
} from '@app/redux/selectors';
// Import props.
import {
	OptionalSectionKeyProps,
	SectionProps,
} from '@app/props';
// Import api models.
import {
	PublicPolicyViewSection,
	SectionStatusInfo,
} from '@app/models/api';
import {
	PublicPolicyViewSectionName,
    SectionStatus
} from '@app/enumerations';

interface ICreatePublicPolicyViewParameters {
	isEditable: boolean;
	friendlyName: string;
	sectionName: PublicPolicyViewSectionName;
	sectionKey?: string;
	errorMessages?: string[];
};

// Utility method for creating public policy view sections.
const createPublicPolicyViewSection = (params: ICreatePublicPolicyViewParameters): PublicPolicyViewSection => {
	// Setup the section status info.
	let publicPolicyViewSectionStatusInfo: SectionStatusInfo = new SectionStatusInfo();
	publicPolicyViewSectionStatusInfo.statusCode = SectionStatus.NOT_REQUIRED;
	publicPolicyViewSectionStatusInfo.statusMessage = String.empty();

	// Set up the section data.
	let section: PublicPolicyViewSection = new PublicPolicyViewSection();
	section.friendlyName = params.friendlyName;
	section.isEditable = params.isEditable;
	section.key = params.sectionKey ?? null;
	section.name = params.sectionName;
	section.status = publicPolicyViewSectionStatusInfo;

	return section;
}

// Fetches the public policy view section name.
const getPublicPolicyViewSectionName = (state: AppState, props: SectionProps<PublicPolicyViewSectionName>): PublicPolicyViewSectionName => {
	return props.sectionName;
}

// Fetches the optional section key prop.
const getOptionalPublicPolicyViewSectionKey = (state: AppState, props: OptionalSectionKeyProps): string | undefined => {
	return props.sectionKey;
}

// Get Public Policy View Mortgagee Sections Selector.
const makeGetPublicPolicyViewMortgageeSectionsSelector = () => createSelector(
	[
		makeIsPublicPolicyActiveForChangesSelector(),
		makeGetPublicPolicyViewMortgageeGuidsSelector(),
	],
	(
		isPublicPolicyActiveForChanges: boolean,
		mortgageeGuids: string[],
	): PublicPolicyViewSection[] => {
		return mortgageeGuids.map((mortgageeGuid: string) => {
			// Create section.
			return createPublicPolicyViewSection({
				isEditable: isPublicPolicyActiveForChanges,
				friendlyName: 'Mortgagee',
				sectionName: PublicPolicyViewSectionName.MORTGAGEE,
				sectionKey: mortgageeGuid,
				errorMessages: [],
			});
		});
	}
);

// Get Public Policy View Landing Section Selector.
const makeGetPublicPolicyViewLandingSectionSelector = () => createSelector(
	[
		makeGetPublicPolicyViewLandingRelatedSectionsSelector(),
		makeIsPublicPolicyActiveForChangesSelector(),
	],
	(
		landingRelatedSections: PublicPolicyViewSection[],
		isPublicPolicyActiveForChanges: boolean,
	): PublicPolicyViewSection => {
		let areAnyLandingRelatedSectionsEditable: boolean = true;

		if (landingRelatedSections.length > 0) {
			areAnyLandingRelatedSectionsEditable = landingRelatedSections.some((landingRelatedSection: PublicPolicyViewSection) => landingRelatedSection.isEditable);
		}

		// Create section.
		return createPublicPolicyViewSection({
			isEditable: areAnyLandingRelatedSectionsEditable && isPublicPolicyActiveForChanges,
			friendlyName: 'Landing',
			sectionName: PublicPolicyViewSectionName.LANDING,
			errorMessages: ['One or more questions in the information below require your attention.  Please review and correct your answers.'],
		});
	}
)

// Get Public Policy View Add Mortgagee Section Selector.
const makeGetPublicPolicyViewAddMortgageeSectionSelector = () => createSelector(
	[
		makeIsPublicPolicyActiveForChangesSelector(),
	],
	(
		isPublicPolicyActiveForChanges: boolean,
	): PublicPolicyViewSection => {
		// Create section.
		return createPublicPolicyViewSection({
			isEditable: isPublicPolicyActiveForChanges,
			friendlyName: 'Add Mortgagee',
			sectionName: PublicPolicyViewSectionName.ADD_MORTGAGEE,
		});
	}
);

// Get Public Policy View Landing Related Sections Selector.
const makeGetPublicPolicyViewLandingRelatedSectionsSelector = () => createSelector(
	[
		makeGetPublicPolicyViewMortgageeSectionsSelector(),
	],
	(
		mortgageeSections: PublicPolicyViewSection[],
	): PublicPolicyViewSection[] => {
		return [
			...mortgageeSections,
		];
	}
);

// Get Public Policy View Sections Selector.
const makeGetPublicPolicyViewSectionsSelector = () => createSelector(
	[
		makeGetPublicPolicyViewLandingRelatedSectionsSelector(),
		makeGetPublicPolicyViewLandingSectionSelector(),
		makeGetPublicPolicyViewAddMortgageeSectionSelector(),
	],
	(
		landingRelatedSections: PublicPolicyViewSection[],
		landingSection: PublicPolicyViewSection,
		addMortgageeSection: PublicPolicyViewSection,
	): PublicPolicyViewSection[] => {
		return [
			...landingRelatedSections,
			landingSection,
			addMortgageeSection,
		];
	}
);

// Is Public Policy View Section Complete Selector.
const makeIsPublicPolicyViewSectionCompleteSelector = () => createSelector(
	[
		getPublicPolicyViewSectionName,
		getOptionalPublicPolicyViewSectionKey,
		makeGetPublicPolicyViewSectionsSelector()
	],
	(
		publicPolicyViewSectionName: PublicPolicyViewSectionName,
		publicPolicyViewSectionKey: string | undefined,
		publicPolicyViewSections: PublicPolicyViewSection[]
	): boolean => {
		let matchingPublicPolicyViewSection: PublicPolicyViewSection | undefined = publicPolicyViewSections.find((publicPolicyViewSection: PublicPolicyViewSection) => {
			return publicPolicyViewSection.name === publicPolicyViewSectionName
				&& publicPolicyViewSection.status?.statusCode === SectionStatus.COMPLETE
				&& ((publicPolicyViewSectionKey === undefined && publicPolicyViewSection.key === null)
					|| publicPolicyViewSection.key === publicPolicyViewSectionKey);
		});

		return matchingPublicPolicyViewSection !== undefined;
	}
);

// Is Public Policy View Section Accessible Selector.
const makeIsPublicPolicyViewSectionAccessibleSelector = () => createSelector(
	[
		getPublicPolicyViewSectionName,
		getOptionalPublicPolicyViewSectionKey,
		makeGetPublicPolicyViewSectionsSelector()
	],
	(
		publicPolicyViewSectionName: PublicPolicyViewSectionName,
		publicPolicyViewSectionKey: string | undefined,
		publicPolicyViewSections: PublicPolicyViewSection[]
	): boolean => {
		let matchingPublicPolicyViewSection: PublicPolicyViewSection | undefined = publicPolicyViewSections.find((publicPolicyViewSection: PublicPolicyViewSection) => {
			return publicPolicyViewSection.name === publicPolicyViewSectionName
				&& publicPolicyViewSection.status?.statusCode !== SectionStatus.NOT_ELIGIBLE
				&& ((publicPolicyViewSectionKey === undefined && publicPolicyViewSection.key === null)
					|| publicPolicyViewSection.key === publicPolicyViewSectionKey);
		});

		return matchingPublicPolicyViewSection !== undefined;
	}
);

// Is Public Policy View Section Editable Selector.
const makeIsPublicPolicyViewSectionEditableSelector = () => createSelector(
	[
		getPublicPolicyViewSectionName,
		getOptionalPublicPolicyViewSectionKey,
		makeGetPublicPolicyViewSectionsSelector()
	],
	(
		publicPolicyViewSectionName: PublicPolicyViewSectionName,
		publicPolicyViewSectionKey: string | undefined,
		publicPolicyViewSections: PublicPolicyViewSection[]
	): boolean => {
		let matchingPublicPolicyViewSection: PublicPolicyViewSection | undefined = publicPolicyViewSections.find((publicPolicyViewSection: PublicPolicyViewSection) => {
			return publicPolicyViewSection.name === publicPolicyViewSectionName
				&& publicPolicyViewSection.isEditable
				&& ((publicPolicyViewSectionKey === undefined && publicPolicyViewSection.key === null)
					|| publicPolicyViewSection.key === publicPolicyViewSectionKey);
		});

		return matchingPublicPolicyViewSection !== undefined;
	}
);

// Get Public Policy View Section Status Message Selector.
const makeGetPublicPolicyViewSectionStatusMessageSelector = () => createSelector(
	[
		getPublicPolicyViewSectionName,
		getOptionalPublicPolicyViewSectionKey,
		makeGetPublicPolicyViewSectionsSelector()
	],
	(
		publicPolicyViewSectionName: PublicPolicyViewSectionName,
		publicPolicyViewSectionKey: string | undefined,
		publicPolicyViewSections: PublicPolicyViewSection[]
	): string | undefined => {
		let matchingPublicPolicyViewSection: PublicPolicyViewSection | undefined = publicPolicyViewSections.find((publicPolicyViewSection: PublicPolicyViewSection) => {
			return publicPolicyViewSection.name === publicPolicyViewSectionName
				&& ((publicPolicyViewSectionKey === undefined && publicPolicyViewSection.key === null)
					|| publicPolicyViewSection.key === publicPolicyViewSectionKey);
		});

		return matchingPublicPolicyViewSection?.status?.statusMessage ?? undefined;
	}
);

// Export composed methods.
export {
	getPublicPolicyViewSectionName,
	getOptionalPublicPolicyViewSectionKey,
};

// export selectors.
export {
	makeGetPublicPolicyViewMortgageeSectionsSelector,
	makeGetPublicPolicyViewLandingSectionSelector,
	makeGetPublicPolicyViewAddMortgageeSectionSelector,
	makeGetPublicPolicyViewLandingRelatedSectionsSelector,
	makeGetPublicPolicyViewSectionsSelector,
	makeIsPublicPolicyViewSectionCompleteSelector,
	makeIsPublicPolicyViewSectionAccessibleSelector,
	makeIsPublicPolicyViewSectionEditableSelector,
	makeGetPublicPolicyViewSectionStatusMessageSelector,
};